import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { passwordRequirements } from '../../utils/userInputUtils';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { passwordResetRequest } from '../../redux/coach/coachSlice';
import OtpInput from '../OtpInput/OtpInput';
import { isInteger } from '../../utils/utils';
import {
  setForgetPasswordCode,
  setForgetPasswordEmail,
} from '../../redux/forgetPassword/forgetPasswordSlice';
import LabeledInput from '../Common/LabeledInput';
import StyledButton from '../Common/StyledButton';

const StyledTextButton = styled(Button)({
  color: '#3B667E',
  fontWeight: 700,
  fontSize: '15px !important',
  padding: '0px 10px !important',
});

interface IUpdatePasswordFormProps {
  mode?: 'CHANGE_PASSWORD' | 'RESET_PASSWORD';
  onPasswordChange?: (_password: string) => void;
}

interface IRequirement {
  text: string;
  isValid: () => boolean;
}

export interface IPasswordRequirementsProps {
  requirements: IRequirement[];
}

const VERIFICATION_CODE_LENGTH = 6;

export const PasswordRequirements = ({ requirements }: IPasswordRequirementsProps) => (
  <List
    dense
    sx={{
      padding: '0px',
    }}
  >
    {requirements.slice(0, 5).map((requirement) => (
      <ListItem
        key={requirement.text}
        sx={{
          padding: '0px',
          marginTop: '12px',
        }}
      >
        <ListItemIcon
          sx={{
            margin: '0px',
            padding: '0px',
            minWidth: 'auto',
            marginRight: '8px',
          }}
        >
          {requirement.isValid() ? (
            <img src="/assets/icons/check-circle-new.svg" alt="check-circle-new" />
          ) : (
            <img src="/assets/icons/close-circle.svg" alt="close-circle" />
          )}
        </ListItemIcon>
        <Typography variant="bodyRegular" color="primary">
          {requirement.text}
        </Typography>
      </ListItem>
    ))}
  </List>
);

const UpdatePasswordForm = ({ mode, onPasswordChange }: IUpdatePasswordFormProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState<string>();
  // const [passwordMismatch, setPasswordMismatch] = useState(false);
  const coachEmail = useAppSelector((state) => state.coach.coach?.email);
  const forgetPasswordLoading = useAppSelector((state) => state.forgetPassword.loading);
  const forgetPasswordError = useAppSelector((state) => state.forgetPassword.error);
  const email = useAppSelector((state) => state.forgetPassword.email);

  const [allValid, setAllValid] = useState(false);

  const isResetPassword = mode === 'RESET_PASSWORD';

  useEffect(() => {
    let isAllValid = passwordRequirements(password, confirmPassword).every((requirement) =>
      requirement.isValid()
    );

    if (isResetPassword) {
      isAllValid =
        isAllValid && !!verificationCode && verificationCode.length === VERIFICATION_CODE_LENGTH;
    }

    setAllValid(isAllValid);
  }, [password, confirmPassword, verificationCode]);

  const currentPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
  };

  async function updatePassword() {
    if (!coachEmail) return;
    const username = coachEmail;
    const oldPassword = currentPassword;
    const newPassword = password;
    dispatch(
      passwordResetRequest({
        username,
        oldPassword,
        newPassword,
        confirmPassword,
      })
    );
  }

  const updatePasswordHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (onPasswordChange) {
      onPasswordChange(password);
    }

    if (isResetPassword) {
      return;
    }

    updatePassword();
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const onVerificationCodeEnter = (code: string) => {
    if (!code || code.length !== VERIFICATION_CODE_LENGTH) return;

    dispatch(setForgetPasswordCode(code));
  };

  // Timer
  const [seconds, setSeconds] = useState(30);

  const formatTime = (time: number) => (time < 10 ? `0${time}` : time);
  useEffect(() => {
    const countdown = setInterval(() => {
      setSeconds((prevSeconds) => prevSeconds - 1);
    }, 1000);

    return () => clearInterval(countdown);
  }, []);

  const handleResendResetCode = () => {
    if (!email) return;
    dispatch(setForgetPasswordEmail(email));
    setSeconds(30);
  };

  return (
    <Grid display="flex">
      <Grid item xs={5} display="flex" flexDirection="column" justifyContent="center">
        <Typography variant="bodyBold" color="primary">
          {`${t('changePassword.passwordValidations.PasswordMustContain')}`}
        </Typography>
        <PasswordRequirements requirements={passwordRequirements(password, confirmPassword)} />
      </Grid>

      <Box
        style={{
          margin: '0 36px',
          width: '1px',
          background: 'linear-gradient(175deg, #FEE4DB -9.81%, #FFAAA0 96.32%)',
          opacity: 0.5,
        }}
      />

      <Grid
        item
        component="form"
        noValidate
        autoComplete="off"
        xs={7}
        display="flex"
        flexDirection="column"
      >
        {isResetPassword && (
          <Typography variant="title2Bold" mb="36px" color="primary">
            {`${t('changePassword.createNewPassword')}`}
          </Typography>
        )}
        {isResetPassword && (
          <Typography variant="bodyBold" color="primary">
            {`${t('changePassword.enterVerificationCode')}`}{' '}
            <Typography variant="bodyBold" textAlign="center" color="#7CAEBE">
              {email}
            </Typography>
          </Typography>
        )}
        {isResetPassword && (
          <Box display="flex" flexDirection="column" gap="4px" mt="32px" mb="16px">
            <Typography variant="meta">{`${t('changePassword.verificationCode')}`}</Typography>
            <OtpInput
              autoFocus
              isValid
              length={VERIFICATION_CODE_LENGTH}
              value={verificationCode}
              validateChar={isInteger}
              onChange={setVerificationCode}
              onComplete={onVerificationCodeEnter}
              gap="4px"
            />
          </Box>
        )}

        {!isResetPassword && (
          <Grid sx={{ width: '100%', mb: '16px' }}>
            <LabeledInput
              label={t('changePassword.currentPassword')}
              required
              name="currentPassword"
              placeholder={t('changePassword.currentPassword')}
              type={showPassword ? 'text' : 'password'}
              id="current-password"
              value={currentPassword}
              handleChange={currentPasswordChange}
              startIcon={<img src="/assets/icons/security.svg" alt="email" />}
              endIcon={
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                >
                  {showNewPassword ? (
                    <img src="/assets/icons/visibility.svg" alt="visibility" />
                  ) : (
                    <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                  )}
                </IconButton>
              }
            />
          </Grid>
        )}
        <Grid sx={{ width: '100%', '& > *:not(:last-child)': { mb: '16px' } }}>
          <LabeledInput
            label={isResetPassword ? t('auth.password') : t('changePassword.newPassword')}
            required
            name="newPassword"
            placeholder={t('auth.password')}
            type={showNewPassword ? 'text' : 'password'}
            id="new-password"
            value={password}
            handleChange={handlePasswordChange}
            startIcon={<img src="/assets/icons/security.svg" alt="email" />}
            endIcon={
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowNewPassword}
              >
                {showNewPassword ? (
                  <img src="/assets/icons/visibility.svg" alt="visibility" />
                ) : (
                  <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                )}
              </IconButton>
            }
          />
          <LabeledInput
            label={
              isResetPassword
                ? t('changePassword.confirmPassword')
                : t('changePassword.confirmNewPassword')
            }
            required
            name="confirmPassword"
            placeholder={t('changePassword.confirmPassword')}
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirm-password"
            value={confirmPassword}
            handleChange={handleConfirmPasswordChange}
            startIcon={<img src="/assets/icons/security.svg" alt="email" />}
            endIcon={
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowConfirmPassword}
              >
                {showConfirmPassword ? (
                  <img src="/assets/icons/visibility.svg" alt="visibility" />
                ) : (
                  <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                )}
              </IconButton>
            }
          />
        </Grid>
        <Grid mt="24px">
          <StyledButton
            disabled={!allValid || (isResetPassword && forgetPasswordLoading)}
            fullWidth
            onClick={updatePasswordHandler}
          >
            {isResetPassword
              ? t('changePassword.createNewPassword')
              : t('changePassword.updatePassword')}
          </StyledButton>
        </Grid>

        {isResetPassword &&
          (seconds > 0 && !forgetPasswordError ? (
            <Grid mt="24px">
              <Typography fontSize="15px" color="#7CAEBE">
                {`${t('changePassword.timeRemaining')} ${Math.floor(seconds / 60)}:${formatTime(
                  seconds % 60
                )}`}
              </Typography>
            </Grid>
          ) : (
            <Grid mt="24px" display="flex">
              <Typography fontSize="15px" color="#7CAEBE">
                {`${t('changePassword.resendRestLinkText')}`}{' '}
              </Typography>
              <StyledTextButton variant="text" onClick={handleResendResetCode}>
                {`${t('changePassword.resendResetLink')}`}{' '}
              </StyledTextButton>
            </Grid>
          ))}
      </Grid>
    </Grid>
  );
};

export default UpdatePasswordForm;
